import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { isAxiosError } from 'axios';
import * as Sentry from '@sentry/vue';
import api from '@/services/api';
import {
    isConfirmedSessionExpiryError,
    isRetryableAuthError,
} from '@/utils/authNetwork';
import type { User } from '@/types';
import { clearPersistedAuthSession } from '@/utils/authSession';
import { setCsrfTokenFallback } from '@/utils/csrfToken';

export interface AuthResponse {
    access_token?: string; // Returned by server but no longer stored client-side; kept for type compat
    refresh_token?: string;
    user: User;
}

// Helper to safely parse stored user profile (non-sensitive display data only)
function getStoredUser(): User | null {
    try {
        const stored = localStorage.getItem('user');
        if (!stored) return null;

        const parsed = JSON.parse(stored);

        if (parsed && typeof parsed.id === 'string' && typeof parsed.email === 'string') {
            return parsed;
        }

        if (import.meta.env.DEV) {
            console.warn('[Auth] Clearing corrupted user data from localStorage');
        }
        localStorage.removeItem('user');
        return null;
    } catch {
        if (import.meta.env.DEV) {
            console.warn('[Auth] Failed to parse user data, clearing localStorage');
        }
        localStorage.removeItem('user');
        return null;
    }
}

export const useAuthStore = defineStore('auth', () => {
    const SESSION_UNCERTAIN_THRESHOLD = 3;

    const user = ref<User | null>(getStoredUser());
    const sessionUncertainCount = ref(0);

    // Restore Sentry user context on page load if the session was already active
    if (user.value) {
        Sentry.setUser({ id: user.value.id, email: user.value.email });
    }
    // Auth state is derived solely from user presence (cookie is httpOnly, not readable by JS)
    const isAuthenticated = computed(() => !!user.value);

    /**
     * Login handler for email/password login response.
     * Tokens are now set as httpOnly cookies by the server.
     * We only store the user profile locally for display purposes.
     */
    function login(data: AuthResponse) {
        user.value = data.user;
        sessionUncertainCount.value = 0;
        localStorage.setItem('user', JSON.stringify(data.user));
        Sentry.setUser({ id: data.user.id, email: data.user.email });
    }

    async function fetchUser() {
        try {
            const response = await api.get('/api/v1/auth/me', { skipAuthHandler: true } as any);
            const rawData = response.data as Record<string, unknown> | null;

            if (!rawData || typeof rawData !== 'object' ||
                !('id' in rawData) || !('email' in rawData)) {
                if (import.meta.env.DEV) {
                    console.error('[Auth] Invalid user data received from API');
                }
                throw new Error('Invalid user data received from API');
            }

            const userData = rawData as unknown as User;
            user.value = userData;
            sessionUncertainCount.value = 0;
            localStorage.setItem('user', JSON.stringify(userData));
            Sentry.setUser({ id: userData.id, email: userData.email });
            return userData;
        } catch (error) {
            if (import.meta.env.DEV) {
                console.error('[Auth] Failed to fetch user:', error);
            }
            throw error;
        }
    }

    async function fetchCsrfToken(): Promise<void> {
        try {
            const response = await api.get('/api/v1/auth/csrf-token');
            const hasCookie = document.cookie.includes('csrf_token=');

            // If the browser didn't set the cookie, use the response body as a fallback
            if (!hasCookie && response.data && typeof response.data === 'object' && 'csrf_token' in response.data) {
                setCsrfTokenFallback((response.data as { csrf_token: string }).csrf_token);
                console.warn('[Auth] CSRF cookie was not set after fetch. Using fallback token. This may indicate a proxy (like Netskope) is stripping cookies.');
            }
        } catch {
            if (import.meta.env.DEV) {
                console.warn('[Auth] Failed to fetch CSRF token');
            }
        }
    }

    function markSessionUncertain(): void {
        sessionUncertainCount.value += 1;
    }

    function clearSession(): void {
        user.value = null;
        sessionUncertainCount.value = 0;
        clearPersistedAuthSession();
        Sentry.setUser(null);
    }

    async function validateSession() {
        const maxAttempts = 2;

        // Try to validate via /me endpoint (works with both cookie and Bearer)
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
                await api.get('/api/v1/auth/me', {
                    skipAuthHandler: true,
                    timeout: 15000,
                } as any);
                sessionUncertainCount.value = 0;
                return true;
            } catch (error: unknown) {
                const isUnauthorized = isAxiosError(error) && error.response?.status === 401;

                // Conservative handling: one immediate retry to avoid false sign-out
                // on transient proxy/network auth glitches.
                if (isUnauthorized && attempt < maxAttempts) {
                    continue;
                }

                if (isUnauthorized) {
                    if (isConfirmedSessionExpiryError(error)) {
                        if (import.meta.env.DEV) {
                            console.warn('[Auth] Session expiry confirmed by server payload; logging out silently');
                        }
                        await logout();
                        return false;
                    }

                    markSessionUncertain();
                    if (import.meta.env.DEV) {
                        console.warn('[Auth] Unconfirmed 401 during session validation; preserving session state', {
                            uncertainCount: sessionUncertainCount.value,
                        });
                    }

                    if (sessionUncertainCount.value >= SESSION_UNCERTAIN_THRESHOLD) {
                        await logout();
                        return false;
                    }

                    return !!user.value;
                }

                if (isRetryableAuthError(error)) {
                    markSessionUncertain();
                    if (import.meta.env.DEV) {
                        console.warn('[Auth] Transient session validation failure; preserving session state');
                    }
                    return !!user.value;
                }

                // Unknown/non-retryable transport errors: preserve existing session state.
                return !!user.value;
            }
        }

        return !!user.value;
    }

    async function logout() {
        try {
            // Call server logout to clear httpOnly cookie
            await api.post('/api/v1/auth/logout', {});
        } catch {
            // Proceed with local cleanup even if server call fails
        }
        clearSession();
    }

    async function verifyEmail(verifyToken: string) {
        try {
            const response = await api.post('/api/v1/auth/verify', { token: verifyToken });
            const data = response.data as AuthResponse;
            if (data.user) {
                user.value = data.user;
                localStorage.setItem('user', JSON.stringify(data.user));
            }
            return data;
        } catch (error: any) {
            throw error.response?.data || error;
        }
    }

    async function requestVerification(email: string) {
        try {
            await api.post('/api/v1/auth/request-verify-token', { email });
            return true;
        } catch {
            throw new Error('Failed to request verification');
        }
    }

    async function forgotPassword(email: string) {
        try {
            await api.post('/api/v1/auth/forgot-password', { email });
            return true;
        } catch {
            throw new Error('Failed to send reset email');
        }
    }

    async function resetPassword(resetToken: string, password: string) {
        try {
            await api.post('/api/v1/auth/reset-password', { token: resetToken, password });
            return true;
        } catch {
            throw new Error('Failed to reset password');
        }
    }

    return {
        user,
        isAuthenticated,
        login,
        fetchUser,
        fetchCsrfToken,
        logout,
        clearSession,
        markSessionUncertain,
        sessionUncertainCount,
        verifyEmail,
        requestVerification,
        forgotPassword,
        resetPassword,
        validateSession,
    };
});
